import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  getGateWorkflow,
  listProjectInsightsMessages,
  type Gate,
  type SelaDecisionBriefContextSummary,
  type SelaProjectDetail,
} from '../../services/selaApi';
import { findLatestDecisionBrief, SelaProjectContextSection } from './SelaProjectContextSection';

const SelaSiteMap = React.lazy(() => import('./SelaSiteMap').then((module) => ({ default: module.SelaSiteMap })));

const INSIGHTS_POLL_INTERVAL_MS = 3000;

type OverviewOutletContext = {
  project: SelaProjectDetail;
  intelKickoffInProgress?: boolean;
  intelKickoffError?: string | null;
};

const STATUS_LABEL: Record<string, string> = {
  locked: 'Locked',
  active: 'Active',
  ready: 'Ready',
  complete: 'Complete',
  blocked: 'Blocked',
};

const STATUS_TONE: Record<string, string> = {
  locked: 'bg-slate-100 text-slate-500',
  active: 'bg-blue-50 text-blue-700',
  ready: 'bg-emerald-50 text-emerald-700',
  complete: 'bg-emerald-50 text-emerald-700',
  blocked: 'bg-rose-50 text-rose-700',
};

export function SelaProjectOverviewPage() {
  const { project, intelKickoffInProgress, intelKickoffError } = useOutletContext<OverviewOutletContext>();
  const primarySite = project.primarySite;
  const [gates, setGates] = useState<Gate[]>([]);
  const [projectContext, setProjectContext] = useState<SelaDecisionBriefContextSummary | null>(null);
  const [contextPending, setContextPending] = useState(false);

  const refreshProjectContext = useCallback(async () => {
    try {
      const messages = await listProjectInsightsMessages(project.id);
      const latestBrief = findLatestDecisionBrief(messages);
      setProjectContext(latestBrief?.contextSummary ?? null);
      setContextPending(
        messages.some((message) => message.kind === 'overview' && message.status === 'pending'),
      );
    } catch {
      setProjectContext(null);
      setContextPending(false);
    }
  }, [project.id]);

  useEffect(() => {
    void refreshProjectContext();
  }, [refreshProjectContext]);

  useEffect(() => {
    if (!intelKickoffInProgress && !contextPending) {
      return;
    }
    const interval = window.setInterval(() => {
      void refreshProjectContext();
    }, INSIGHTS_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [contextPending, intelKickoffInProgress, refreshProjectContext]);

  useEffect(() => {
    let cancelled = false;
    getGateWorkflow(project.id)
      .then((workflow) => {
        if (cancelled) return;
        setGates(workflow.gates || []);
      })
      .catch(() => {
        if (cancelled) return;
        setGates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [project.id]);

  const gateSummary = useMemo(() => {
    const activeGates = gates.filter((gate) => !gate.preview);
    const activeGate = activeGates.find((gate) => ['blocked', 'in_progress', 'needs_review', 'active', 'ready'].includes(gate.status)) || null;
    return { activeGate, activeGates };
  }, [gates]);

  return (
    <main className="flex-1 overflow-auto p-6 md:p-8">
      <Link
        to="/"
        className="mb-6 inline-flex text-sm font-medium text-[#3251b7] hover:text-[#1e4474] focus:outline-none focus:ring-2 focus:ring-[#3251b7] focus:ring-offset-2 rounded"
      >
        ← All projects
      </Link>

      <div className="mb-8">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="rounded-md bg-[#f0f1f3] px-2 py-0.5 text-[11px] font-medium text-[#475669]">
            {project.typeLabel}
          </span>
          <span className="rounded-md bg-[#dbebff] px-2 py-0.5 text-[11px] font-semibold text-[#2570ee]">
            {project.activeStageLabel}
          </span>
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-[#0f172a] md:text-3xl">{project.title}</h1>
        <p className="mt-2 text-sm text-[#647488] md:text-base">{project.address}</p>
      </div>

      <TimelineOverview
        projectId={project.id}
        gates={gateSummary.activeGates}
        activeGate={gateSummary.activeGate}
      />

      {projectContext ? (
        <SelaProjectContextSection context={projectContext} />
      ) : intelKickoffInProgress || contextPending ? (
        <section className="mb-8 rounded-2xl border border-dashed border-[#cdd5e1] bg-white px-5 py-4 shadow-sm">
          <h2 className="text-lg font-bold tracking-tight text-[#0f172a]">Project Context</h2>
          <p className="mt-2 text-sm text-[#647488]">
            Project context in progress.
          </p>
        </section>
      ) : null}

      <section className="rounded-xl border border-[#e1e8f0] bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-[#0f172a]">Overview</h2>
        {intelKickoffError ? (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-800">
            {intelKickoffError}
          </div>
        ) : null}

        {primarySite ? (
          <div className="mt-4 space-y-6">
            <Suspense
              fallback={
                <div className="flex min-h-[320px] items-center justify-center rounded-xl border border-[#d8e0eb] bg-[#0f172a] px-6 text-center text-sm text-slate-300">
                  Loading site atlas...
                </div>
              }
            >
              <SelaSiteMap site={primarySite} />
            </Suspense>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-lg border border-[#e1e8f0] bg-[#f7fafd] px-4 py-5">
                <p className="text-xs font-medium uppercase tracking-wide text-[#647488]">Primary site</p>
                <p className="mt-2 text-sm font-semibold text-[#0f172a]">{primarySite.address}</p>
              </div>
              <div className="rounded-lg border border-[#e1e8f0] bg-[#f7fafd] px-4 py-5">
                <p className="text-xs font-medium uppercase tracking-wide text-[#647488]">Zoning</p>
                <p className="mt-2 text-sm font-semibold text-[#0f172a]">
                  {primarySite.summary.zoningCode || 'Unavailable'}
                </p>
              </div>
              <div className="rounded-lg border border-[#e1e8f0] bg-[#f7fafd] px-4 py-5">
                <p className="text-xs font-medium uppercase tracking-wide text-[#647488]">Parcel / permits</p>
                <p className="mt-2 text-sm font-semibold text-[#0f172a]">
                  {primarySite.summary.parcelPin || 'Parcel pending'} · {primarySite.summary.permitCount || 0} permits
                </p>
              </div>
            </div>

          </div>
        ) : (
          <>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#475669]">
              {intelKickoffInProgress
                ? 'Research in progress.'
                : 'This parcel evaluation workspace is ready for saved property context. Run research to populate zoning, overlays, permit history, risks, and next actions.'}
            </p>
          </>
        )}
      </section>
    </main>
  );
}

function TimelineOverview({
  projectId,
  gates,
  activeGate,
}: {
  projectId: string;
  gates: Gate[];
  activeGate: Gate | null;
}) {
  if (gates.length === 0) {
    return (
      <section className="mb-8 rounded-xl border border-dashed border-[#cdd5e1] bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-[#647488]">Diligence Roadmap</p>
            <h2 className="mt-2 text-lg font-semibold text-[#0f172a]">Roadmap in progress.</h2>
          </div>
          <Link to={`/project/${projectId}/timeline`} className="rounded-lg bg-[#3251b7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1e4474]">
            Open Roadmap
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-8 rounded-xl border border-[#e1e8f0] bg-white p-5 shadow-sm">
      <div className="mb-5">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[#647488]">Current roadmap stage</p>
          <p className="mt-2 text-lg font-semibold text-[#0f172a]">{activeGate?.label || 'No active stage'}</p>
        </div>
      </div>

      <div className="overflow-x-auto px-1 pb-2 pt-3">
        <div className="relative flex min-w-max items-start gap-0">
          <div className="absolute left-8 right-8 top-[18px] h-0.5 bg-[#d9e2ef]" aria-hidden="true" />
          {gates.map((gate, index) => {
            const isActive = activeGate?.gateId === gate.gateId;
            const isComplete = gate.status === 'complete' || gate.status === 'ready';
            const isBlocked = gate.status === 'blocked';
            return (
              <Link
                key={gate.gateId}
                to={`/project/${projectId}/timeline`}
                className="relative z-10 flex w-[170px] flex-col items-center px-2 text-center"
              >
                <span
                  className={[
                    'flex h-9 w-9 items-center justify-center rounded-full border-2 bg-white text-xs font-bold shadow-sm transition',
                    isActive ? 'border-[#3251b7] bg-[#3251b7] text-white ring-4 ring-[#dce4ff]' : '',
                    !isActive && isBlocked ? 'border-rose-300 text-rose-700' : '',
                    !isActive && isComplete ? 'border-emerald-300 text-emerald-700' : '',
                    !isActive && !isBlocked && !isComplete ? 'border-[#cdd5e1] text-[#647488]' : '',
                  ].join(' ')}
                >
                  {index + 1}
                </span>
                <span className="mt-2 text-xs font-semibold text-[#0f172a]">{gate.label}</span>
                <span className={['mt-1 rounded-full px-2 py-0.5 text-[10px] font-semibold', STATUS_TONE[gate.status] || STATUS_TONE.locked].join(' ')}>
                  {STATUS_LABEL[gate.status] || gate.status}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

